// ============================================================
// THE DISPLAY FIGURE - one family for everyone: a ring on the ground, a
// stalk, a head (the user's ruling, 2026-10-07). The head stands at half
// the figure's height above whatever it stands on, so size reads as height
// (a Space Marine's head is higher than a guardsman's) and a raised figure
// carries its whole ring, stalk and head up with it.
//
// Pure. Zero React dependency.
// ============================================================
import type { Vec3 } from '../battlefield/types';
import type { Token } from '../battlefield/tokens';
import { tokenHeight } from '../battlefield/tokens';
import type { FigureModel } from './figureModel';
import { headingPoint } from '../battlefield/facing';
import { OBJECT_HALF_HEIGHT } from './heads';

/** How many points along the figure the occlusion test samples. */
const SAMPLES = 5;
/** The lowest sample, just above the feet, so the ground itself never counts as cover. */
const FOOT_CLEARANCE = 0.15;

type Placed = Pick<Token, 'x' | 'z' | 'elevation' | 'eyeHeight'> & { height?: number };
type Lying = Placed & { footprint: number; heading?: number };

/**
 * Where the head is: half the figure's height above its ground. The height is
 * the one cover reads (`tokenHeight`: a token written before v7.17.0 has only
 * its eye height), so the display and the rules agree on how tall it is.
 */
export function headPoint(token: Placed): Vec3 {
  return { x: token.x, y: token.elevation + tokenHeight(token) / 2, z: token.z };
}

/** Points from just above the feet to the head: what occlusion asks about. */
export function figureSamples(token: Placed): Vec3[] {
  const top = headPoint(token).y;
  const bottom = token.elevation + FOOT_CLEARANCE;
  return Array.from({ length: SAMPLES }, (_, i) => ({
    x: token.x,
    y: bottom + ((top - bottom) * i) / (SAMPLES - 1),
    z: token.z,
  }));
}

/** How high above its ground a lying head, or a burrower's, rests: on the floor, not in it. */
const ON_THE_GROUND = 0.1;
/** A walker's head is held high: this share of its height (the user's "hexagonal prism held high"). */
const WALKER_HIGH = 0.85;
/** A flyer flies this far above its own height. */
const FLIGHT = 1.2;
/** A hoverer rides this far off the ground, ring and all (the traits page, design-gaps T1). */
export const HOVER = 0.35;
/** A burrower's head is under the floor by this share of its height (the traits page: -1.2 m for a man). */
const BURROW_SHARE = 0.65;
/** A lying body is this many radii long from its centre to each end's tip, head at one end. */
const BODY_LENGTH = 2.4;

/** The figure's drawn radius: half the sheet's Token size when set, else the footprint (a radius). */
export const drawnRadius = (token: { footprint: number }, model: FigureModel) =>
  model.diameter ? model.diameter / 2 : token.footprint;

/** What Token size does to the figure's height: a figure drawn larger than its footprint stands taller too. */
const sizeScale = (token: { footprint: number }, model: FigureModel) =>
  Math.max(1, drawnRadius(token, model) / token.footprint);

/** Does this figure lie in a capsule? Not the amorphous (it slumps into a puddle), nor a crowd (it stays a crowd). */
export const liesInCapsule = (model: FigureModel) =>
  model.posture !== 'stand' && model.klass !== 'amorphous' && model.klass !== 'object' && !model.crowd;

/**
 * Where the head sits, by the figure model (plan map-display, B): lying in a
 * capsule - prone, helpless or dead - it rests on the ground at the far end
 * of the body along the heading; the amorphous and a crowd lie where they
 * stood; a burrower's is under the floor; a walker holds it high; a flyer is
 * above it all; a hoverer's rides with its lifted ring. Otherwise at half the
 * height. Token size scales the height with the ring.
 */
export function figureHead(token: Lying, model: FigureModel): Vec3 {
  const ground = token.elevation;
  // An Object (2026-10-26) has no stalk and no posture: its diamond rests on its bottom tip, wherever it stands.
  // Its diamond keeps its own size (the footprint) whatever ring a box gives it, and perches on the box's top.
  if (model.klass === 'object') {
    return { x: token.x, y: ground + (model.perch ?? 0) + OBJECT_HALF_HEIGHT * token.footprint, z: token.z };
  }
  if (liesInCapsule(model)) {
    const end = headingPoint(token, token.heading ?? 0, halfLength(drawnRadius(token, model)));
    return { ...end, y: ground + ON_THE_GROUND };
  }
  if (model.posture !== 'stand') return { x: token.x, y: ground + ON_THE_GROUND, z: token.z };
  const height = tokenHeight(token) * sizeScale(token, model);
  if (model.trait === 'burrower') return { x: token.x, y: ground - height * BURROW_SHARE, z: token.z };
  if (model.trait === 'flyer') return { x: token.x, y: ground + height + FLIGHT, z: token.z };
  const lift = liftOf(model);
  return { x: token.x, y: ground + lift + height * (model.klass === 'walker' ? WALKER_HIGH : 0.5), z: token.z };
}

/** A hoverer standing rides off the ground; anything else stands on it. */
const liftOf = (model: FigureModel) => (model.trait === 'hoverer' && model.posture === 'stand' ? HOVER : 0);

/** Where the figure's ring is drawn: on its ground, or a hoverer's just off it (design-gaps T1). */
export function figureFoot(token: Pick<Token, 'x' | 'z' | 'elevation'>, model: FigureModel): Vec3 {
  return { x: token.x, y: token.elevation + liftOf(model), z: token.z };
}

/** From a lying body's centre to the centre of each end's round: BODY_LENGTH radii to its tip, less the round. */
const halfLength = (r: number) => (BODY_LENGTH - 1) * r;

/**
 * The lying body's outline: a stadium on the ground, as wide as the standing
 * ring (radius `r`) and BODY_LENGTH radii long along the heading, centred on
 * the token - the ring stretched along the fall (the posture family, ruling
 * 2026-10-07). Ground points, for the renderer to project.
 */
export function lyingCapsule(token: Lying, r: number): Vec3[] {
  const half = halfLength(r);
  const h = ((token.heading ?? 0) * Math.PI) / 180;
  const arc = (cx: number, from: number) =>
    Array.from({ length: 7 }, (_, i) => {
      const a = from + (i * Math.PI) / 6;
      return [cx + r * Math.cos(a), r * Math.sin(a)] as const;
    });
  const local = [...arc(half, -Math.PI / 2), ...arc(-half, Math.PI / 2)];
  return local.map(([lx, lz]) => ({
    x: token.x + lx * Math.cos(h) - lz * Math.sin(h),
    y: token.elevation,
    z: token.z + lx * Math.sin(h) + lz * Math.cos(h),
  }));
}
